import { useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router";
import { useQuery } from "@apollo/client/react";
import { PublishedQuestionnaireGroupsDocument } from "@/graphql/generated/operations";
import { BackButton } from "@/components/common/BackButton";
import { NoBackend } from "@/components/common/NoBackend";
import { TileArtwork } from "@/components/grow/TileArtwork";
import {
  estimateDurationMinutes,
  groupImageSrc,
  localizedText,
  tileHueFromId,
} from "@/lib/questionnaire-group";
import { useAssistantContext } from "@/lib/use-assistant-context";
import { useAssistantTour } from "@/lib/use-assistant-tour";

// Grow · Play (workshop snapshot screens/GrowPlay.jsx).
//
// The catalogue has one source: PUBLISHED questionnaire groups from genesis
// (genesis#183, spark#196) - one group is one tile, name, description, artwork
// and order all editorial. The design-spec placeholder games that used to pad
// the grid (Back2Future, Treasure Hunt, ...) are gone: no content was coming for
// them, and a tile that leads nowhere misleads a pilot user more than a shorter,
// honest catalogue does. With every entry a questionnaire, the Solo/Team filter
// and the per-card player label carried no information any more and went with
// them.
//
// Nothing here knows about "the Power Quiz": no fixed id, no fixed title, no
// assumption about how many parts a group has. That knowledge was what made
// every new element-free questionnaire show up as a seventh quiz part.

// A catalogue tile after its editorial copy from genesis has been resolved to
// the request locale.
interface CatalogueEntry {
  id: string;
  target: string;
  kind: string;
  title: string;
  caption: string;
  duration: string;
  hue: number;
  featured?: boolean;
  // Group artwork as a data URI, rendered sharp and format-filling. Absent for
  // groups without artwork, which fall back to the AbstractTile.
  imageSrc?: string;
}

// A questionnaire is always answered alone, so the hero never needs a player
// count from the data model.
const GROUP_PLAYERS = "1";

// The AbstractTile pattern for an unillustrated group. Only the hue varies per
// group (from its id); one shared pattern keeps the catalogue reading as one set.
const GROUP_TILE_VARIANT = "grid";

export function GrowPlayPage() {
  const navigate = useNavigate();
  const intl = useIntl();

  const { data, loading } = useQuery(PublishedQuestionnaireGroupsDocument);

  // Order comes from genesis (position, then name) and is never recomputed here.
  // A published group with no active questions is dropped: its tile would be a
  // click into nothing. That is a display guard - what makes a group visible at
  // all is its PUBLISHED status, not this filter.
  const groups = useMemo(
    () => (data?.publishedQuestionnaireGroups ?? []).filter((g) => g.itemCount > 0),
    [data?.publishedQuestionnaireGroups],
  );

  const entries = useMemo<CatalogueEntry[]>(
    () =>
      groups.map<CatalogueEntry>((group, index) => ({
        id: group.questionnaireGroupId,
        target: `/grow/play/group/${group.questionnaireGroupId}`,
        kind: intl.formatMessage({
          id: "grow.play.group.kind",
          defaultMessage: "Questionnaire",
        }),
        // Editorial copy from genesis, resolved to the request locale. Never
        // translated in the client - only fallen back to the other language when
        // this one was left empty.
        title: localizedText(group.name),
        caption: localizedText(group.description),
        duration: intl.formatMessage(
          { id: "grow.play.group.duration", defaultMessage: "approx. {minutes} min" },
          { minutes: estimateDurationMinutes(group.itemCount) },
        ),
        hue: tileHueFromId(group.questionnaireGroupId),
        // The first published group leads the catalogue; there is no editorial
        // "featured" flag, and hard-coding one entry is what spark#196 removed.
        featured: index === 0,
        imageSrc: groupImageSrc(group.image) ?? undefined,
      })),
    [groups, intl],
  );

  const featured = entries.find((e) => e.featured);
  const rest = entries.filter((e) => !e.featured);
  // Only after the first response: during the initial load the catalogue is
  // simply incomplete, not empty.
  const noGroups = !loading && groups.length === 0;

  const open = (entry: CatalogueEntry) => navigate(entry.target);

  // Live assistant context (motive 1, @external-adr AGENT-SERVICE-0020-ag-ui-protocol-adoption): the arcade is mostly static
  // content, so this describes what's on screen so "describe what you see" is
  // answerable.
  useAssistantContext([
    {
      description: "games shown on the Play page",
      value:
        (featured ? `featured: ${featured.title} (${featured.caption}); ` : "") +
        `others: ${rest.map((e) => e.title).join(", ")}`,
    },
  ]);

  // Guided tour (/tour): the featured questionnaire, then the catalogue. Each stop
  // only exists when its anchor does - there is nothing to point at on an
  // environment without published groups, and no grid when the one group is the
  // hero.
  useAssistantTour(
    entries.length === 0
      ? null
      : {
          label: intl.formatMessage({
            id: "grow.play.tour.label",
            defaultMessage: "Grow Play",
          }),
          summary: intl.formatMessage(
            {
              id: "grow.play.tour.summary",
              defaultMessage:
                "{count, plural, one {This is the Play arcade: one game that gets your powers moving.} other {This is the Play arcade: # games that get your powers moving.}}",
            },
            { count: entries.length },
          ),
          stops: [
            ...(featured
              ? [
                  {
                    anchor: "grow.startQuiz",
                    title: intl.formatMessage({
                      id: "grow.play.tour.featured.title",
                      defaultMessage: "The featured questionnaire",
                    }),
                    description: intl.formatMessage(
                      {
                        id: "grow.play.tour.featured.desc",
                        defaultMessage:
                          "{name} leads the catalogue: {caption} It takes {duration} and you answer it on your own. Click here to start.",
                      },
                      { name: featured.title, caption: featured.caption, duration: featured.duration },
                    ),
                  },
                ]
              : []),
            ...(rest.length > 0
              ? [
                  {
                    anchor: "grow.play.grid",
                    title: intl.formatMessage({
                      id: "grow.play.tour.grid.title",
                      defaultMessage: "All games",
                    }),
                    description: intl.formatMessage(
                      {
                        id: "grow.play.tour.grid.desc",
                        defaultMessage:
                          "The rest of the catalogue holds {count, plural, one {one more game} other {# more games}}. Click a card to start.",
                      },
                      { count: rest.length },
                    ),
                  },
                ]
              : []),
          ],
        },
  );

  return (
    <div className="max-w-[1600px] px-4 pb-12 pt-6 @sm:px-6 @lg:px-10" data-graphql="PublishedQuestionnaireGroups">
      <div className="mb-5 flex items-baseline justify-between">
        <BackButton />
        {loading ? null : (
          <div className="text-[10px] tracking-[0.25em] text-ink/55">
            <FormattedMessage
              id="grow.play.headerCount"
              defaultMessage="{count, plural, one {# game} other {# games}}"
              values={{ count: entries.length }}
            />
          </div>
        )}
      </div>

      <h1 className="font-serif text-5xl leading-tight">
        <FormattedMessage id="grow.play.title" defaultMessage="Play" />
      </h1>
      <p className="mt-3 max-w-xl text-sm text-ink/55">
        <FormattedMessage
          id="grow.play.intro"
          defaultMessage="Playful exercises and quizzes that get your powers moving."
        />
      </p>

      {featured ? (
        <div className="mt-8">
          <div className="mb-3 text-[10px] tracking-[0.25em] text-ink/45">
            <FormattedMessage id="grow.play.featured" defaultMessage="Featured" />
          </div>
          <FeaturedGame entry={featured} onClick={() => open(featured)} />
        </div>
      ) : null}

      {noGroups ? (
        <div className="mt-8">
          <NoBackend
            label={intl.formatMessage({
              id: "grow.play.quiz.missing",
              defaultMessage: "No questionnaires are set up on this environment yet.",
            })}
          />
        </div>
      ) : null}

      {rest.length > 0 ? (
        <div className="mt-8">
          <div className="mb-3 text-[10px] tracking-[0.25em] text-ink/45">
            <FormattedMessage id="grow.play.allGames" defaultMessage="All games" />
          </div>
          <div
            className="grid grid-cols-1 gap-4 @sm:grid-cols-2 @lg:grid-cols-3 @xl:grid-cols-4"
            data-assistant-anchor="grow.play.grid"
          >
            {rest.map((e) => (
              <GameCard key={e.id} entry={e} onClick={() => open(e)} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FeaturedGame({ entry, onClick }: { entry: CatalogueEntry; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      // The Assistant's HighlightElement tool addresses the leading catalogue
      // entry by this anchor; it is a stable UI address, not a claim about which
      // questionnaire sits here.
      data-assistant-anchor="grow.startQuiz"
      className="grid w-full grid-cols-1 overflow-hidden rounded-md bg-surface text-left ring-1 ring-ink/5 transition hover:ring-accent/40 @lg:grid-cols-[minmax(240px,0.75fr)_minmax(0,1.25fr)]"
    >
      <div className="relative">
        <TileArtwork
          imageSrc={entry.imageSrc}
          hue={entry.hue}
          variant={GROUP_TILE_VARIANT}
          className="h-full min-h-[200px]"
        />
        <span className="absolute left-3 top-3 rounded bg-accent px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-accent">
          {entry.kind}
        </span>
      </div>
      <div className="flex flex-col justify-between p-6">
        <div>
          <div className="text-[10px] tracking-[0.25em] text-accent">
            <FormattedMessage id="grow.play.featured" defaultMessage="Featured" />
          </div>
          <h2 className="mt-2 font-serif text-3xl">{entry.title}</h2>
          {entry.caption ? (
            <p className="mt-2 text-sm leading-relaxed text-ink/70">{entry.caption}</p>
          ) : null}
        </div>
        <div className="mt-5 flex items-center gap-4 text-[10px] tracking-wider text-ink/55">
          <span>● {entry.duration}</span>
          <span>
            {/* The hero is always a questionnaire group, and a questionnaire is
                answered alone - there is no plural case to render here. */}
            ● {GROUP_PLAYERS}{" "}
            <FormattedMessage id="grow.play.personSingular" defaultMessage="person" />
          </span>
          <span className="ml-auto text-accent">
            <FormattedMessage id="grow.play.start" defaultMessage="Start ↗" />
          </span>
        </div>
      </div>
    </button>
  );
}

function GameCard({ entry, onClick }: { entry: CatalogueEntry; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      // flex-col, because a bare button centres its content box vertically once
      // the grid stretches it past its own height. Captions are editorial, so a
      // one-line caption next to a two-line one leaves the shorter card's
      // artwork floating half the slack below the top edge.
      className="flex flex-col overflow-hidden rounded-md bg-surface text-left ring-1 ring-ink/5 transition hover:ring-accent/40"
    >
      <div className="relative shrink-0">
        <TileArtwork
          imageSrc={entry.imageSrc}
          hue={entry.hue}
          variant={GROUP_TILE_VARIANT}
          className="aspect-[16/10]"
        />
        <span className="absolute left-2 top-2 rounded bg-ink px-1.5 py-0.5 text-[9px] uppercase tracking-widest text-on-ink">
          {entry.kind}
        </span>
        <span className="absolute bottom-2 right-2 flex h-8 w-8 items-center justify-center rounded-full bg-accent text-on-accent">
          <PlayIcon />
        </span>
      </div>
      <div className="px-4 py-3">
        <div className="text-sm font-medium leading-tight">{entry.title}</div>
        {entry.caption ? (
          <div className="mt-1.5 text-[11px] leading-snug text-ink/55">{entry.caption}</div>
        ) : null}
        <div className="mt-3 text-[10px] tracking-wider text-ink/40">{entry.duration}</div>
      </div>
    </button>
  );
}

function PlayIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}
